import {Injector} from '@angular/core';
import {filter, takeUntil, tap} from 'rxjs/operators';
import {DynamicComponentFactoryService} from '../../../../dynamic-component';
import {
    TablePlugin,
    TablePluginColumnCellMovedArgs,
    TablePluginColumnCellRemovedArgs,
    TablePluginColumnCellRenderedArgs,
    TablePluginColumnConfigAddedArgs,
    TablePluginColumnConfigIdentityChangedArgs,
    TablePluginColumnsSetupArgs,
    TablePluginConfigMovedArgs,
    TablePluginInitTablePluginArgs
} from '../table-plugin';
import {TableColumnsResizerComponent} from './table-columns-resizer.component';

export enum ResizingTablePluginMessageType {
    TOGGLE_RESIZING = 'TOGGLE_RESIZING'
}

export class TableColumnsResizerPlugin implements TablePlugin {

    private tableColumnsResizerComponent: TableColumnsResizerComponent;

    constructor(
        private injector: Injector
    ) {}

    public initTablePlugin(args: TablePluginInitTablePluginArgs): void {
        const dynamicComponentFactoryService = this.injector.get(DynamicComponentFactoryService);

        this.tableColumnsResizerComponent = dynamicComponentFactoryService.create(TableColumnsResizerComponent, {
            injector: this.injector,
            place: {viewContainerRef: args.afterTableViewContainerRef},
            configure: {
                tableBodyRulerDirective: args.tableBodyRulerDirective,
                pointerEventsElementRef: args.pointerEventsElementRef,
                columnsResize$:          args.columnsUpdate$,
                autoResizeColumns$:      args.autoResizeColumns$
            },
            destroyWhen: args.destroy$
        }).instance;

        args.message$.pipe(
            filter(msg => msg.type === ResizingTablePluginMessageType.TOGGLE_RESIZING),
            tap(() =>
                this.tableColumnsResizerComponent.pluginEnabledState$
                    .next(!this.tableColumnsResizerComponent.pluginEnabledState$.value)
            ),
            takeUntil(args.destroy$)
        ).subscribe();
    }

    public columnsSetup(args: TablePluginColumnsSetupArgs): void {
        this.tableColumnsResizerComponent.setupColumns(args);
    }

    public tableReady(): void {
        this.tableColumnsResizerComponent.produceColumnSplitters();
    }

    public finalizeColumnsConfigChanges(): void {
        this.tableColumnsResizerComponent.produceColumnSplitters();
    }

    public finalizeCellsRendering(): void {
        this.tableColumnsResizerComponent.finalizeColumnsRendering();
    }

    public columnConfigAdded(args: TablePluginColumnConfigAddedArgs): void {
        this.tableColumnsResizerComponent.onColumnAdded(args);
    }

    public columnConfigRemoved(adjustedPreviousIndex: number): void {
        this.tableColumnsResizerComponent.onColumnRemoved(adjustedPreviousIndex);
    }

    public columnConfigIdentityChanged(args: TablePluginColumnConfigIdentityChangedArgs): void {
        this.tableColumnsResizerComponent.onColumnConfigIdentityChanged(args.columnConfig, args.currentIndex);
    }

    public columnConfigMoved(args: TablePluginConfigMovedArgs): void {
        this.tableColumnsResizerComponent.onColumnConfigMoved(
            args.previousColumnConfigIndex,
            args.columnConfigIndex
        );
    }

    public columnCellRemoved(args: TablePluginColumnCellRemovedArgs): void {
        this.tableColumnsResizerComponent.onColumnCellRemoved(args.columnConfigIndex, args.adjustedRowIndex);
    }

    public onColumnCellMoved(args: TablePluginColumnCellMovedArgs): void {
        this.tableColumnsResizerComponent.onColumnCellMoved(
            args.columnConfigIndex,
            args.adjustedPreviousRowIndex,
            args.adjustedCurrentRowIndex
        );
    }

    public columnCellRendered(args: TablePluginColumnCellRenderedArgs): void {
        this.tableColumnsResizerComponent.onColumnCellRendered(
            args.viewRef,
            args.columnDirective,
            args.columnConfigIndex,
            args.columnDefIndex,
            args.rowIndex
        );
    }

}
